import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuthStore } from '@/stores/auth-store';

interface UseIdleTimeoutOptions {
  /** Timeout duration in minutes */
  timeoutMinutes: number;
  /** Seconds before full timeout to show the warning */
  warningSeconds?: number;
  /** Called when the timeout is reached */
  onTimeout: () => void;
}

interface UseIdleTimeoutResult {
  /** Whether the warning overlay should be shown */
  isWarning: boolean;
  /** Seconds remaining until auto-logout */
  remainingSeconds: number;
  /** Reset the idle timer (e.g., after user clicks "Stay logged in") */
  resetTimer: () => void;
}

const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'wheel',
] as const;

export function useIdleTimeout({
  timeoutMinutes,
  warningSeconds = 60,
  onTimeout,
}: UseIdleTimeoutOptions): UseIdleTimeoutResult {
  const { isAuthenticated } = useAuthStore();
  const [isWarning, setIsWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  const timeoutMs = timeoutMinutes * 60 * 1000;
  const warningMs = warningSeconds * 1000;

  // Refs to avoid stale closures in event listeners and intervals
  const lastActivityRef = useRef<number>(Date.now());
  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onTimeoutRef = useRef(onTimeout);
  const isAuthenticatedRef = useRef(isAuthenticated);
  const hasTimedOutRef = useRef(false);

  // Keep refs in sync
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    hasTimedOutRef.current = false;
    setIsWarning(false);
    setRemainingSeconds(0);
  }, []);

  // Set up activity listeners
  useEffect(() => {
    if (!isAuthenticated) {
      // Clean up if logged out
      setIsWarning(false);
      setRemainingSeconds(0);
      return;
    }

    const handleActivity = () => {
      lastActivityRef.current = Date.now();
      setIsWarning(false);
      setRemainingSeconds(0);
    };

    // Throttled mousemove to avoid excessive updates
    let moveThrottle: ReturnType<typeof setTimeout> | null = null;
    const handleMouseMove = () => {
      if (!moveThrottle) {
        moveThrottle = setTimeout(() => {
          handleActivity();
          moveThrottle = null;
        }, 300);
      }
    };

    ACTIVITY_EVENTS.forEach((event) => {
      if (event === 'mousemove') {
        window.addEventListener(event, handleMouseMove, { passive: true });
      } else {
        window.addEventListener(event, handleActivity, { passive: true });
      }
    });

    return () => {
      if (moveThrottle) clearTimeout(moveThrottle);
      ACTIVITY_EVENTS.forEach((event) => {
        if (event === 'mousemove') {
          window.removeEventListener(event, handleMouseMove);
        } else {
          window.removeEventListener(event, handleActivity);
        }
      });
    };
  }, [isAuthenticated]);

  // Check timer periodically
  useEffect(() => {
    if (!isAuthenticated) {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      return;
    }

    // Check every second
    checkIntervalRef.current = setInterval(() => {
      const idleTime = Date.now() - lastActivityRef.current;

      if (!isAuthenticatedRef.current) return;

      if (idleTime >= timeoutMs && !hasTimedOutRef.current) {
        // Time's up — log out (guard prevents double-firing before cleanup)
        hasTimedOutRef.current = true;
        setIsWarning(false);
        setRemainingSeconds(0);
        onTimeoutRef.current();
        return;
      }

      if (idleTime >= timeoutMs - warningMs) {
        // In warning zone
        const msLeft = timeoutMs - idleTime;
        const secsLeft = Math.ceil(msLeft / 1000);
        setIsWarning(true);
        setRemainingSeconds(secsLeft);
      }
    }, 1000);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
    };
  }, [isAuthenticated, timeoutMs, warningMs]);

  return { isWarning, remainingSeconds, resetTimer };
}
